/**
 * Login route — passwordless OTP authentication flow with multi-account support.
 *
 * When the saved-accounts registry contains resumable sessions, they are
 * rendered above the email form as a quick-login list. Clicking a saved
 * account attempts a token refresh with the snapshot refresh token and
 * redirects to the appropriate zone on success. If the refresh token has
 * expired, the entry is removed automatically and the OTP login flow is
 * started for that email — sending a verification code and navigating
 * straight to the verify page. If the OTP send fails, the email is
 * prefilled and the user is prompted to try again manually.
 *
 * Clicking the ✕ button on a saved account removes it from the registry
 * without affecting any active server-side session.
 *
 * If the user is already authenticated when they arrive, they are
 * redirected away immediately.
 *
 * @module routes/login
 */

import { removeAccount, type SavedAccount, saveAccount } from '@learnai/api/accounts';
import { defaultRedirectForScopes } from '@learnai/api/auth';
import { useAuth } from '@learnai/api/auth-provider';
import { storage } from '@learnai/api/storage';
import { tryCatch } from '@learnai/tools/try-catch';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { Avatar, AvatarFallback, AvatarImage } from '@learnai/ui/components/ui/avatar';
import { Button } from '@learnai/ui/components/ui/button';
import { ButtonGroup } from '@learnai/ui/components/ui/button-group';
import { Card, CardContent } from '@learnai/ui/components/ui/card';
import { Input } from '@learnai/ui/components/ui/input';
import { Label } from '@learnai/ui/components/ui/label';
import { toast } from '@learnai/ui/components/ui/sonner';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { GoogleIcon } from '@web/assets/brand-icons';
import { api } from '@web/lib/api';
import { resolvePostLoginScopes, safeReturnTo } from '@web/lib/auth-helpers';
import { getUserInitials } from '@web/lib/avatar';
import { arrayBufferToBase64url, base64urlToArrayBuffer } from '@web/lib/webauthn';
import { KeyIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Login() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { isAuthenticated, isLoading, scopes, savedAccounts, refreshSavedAccounts } = useAuth();

	const [email, setEmail] = useState(searchParams.get('email') ?? '');
	const [loading, setLoading] = useState(false);
	const [passkeyLoading, setPasskeyLoading] = useState(false);

	// Multi-account resume state.
	const [resumingId, setResumingId] = useState<string | null>(null);

	const returnTo = safeReturnTo(searchParams.get('returnTo'));
	const hasSavedAccounts = !savedAccounts.isEmpty();
	const isBusy = loading || passkeyLoading || resumingId !== null;

	// -----------------------------------------------------------------------
	// Redirect if already authenticated
	// -----------------------------------------------------------------------

	useEffect(() => {
		if (!isLoading && isAuthenticated) {
			const destination = returnTo ?? defaultRedirectForScopes(scopes);
			window.location.href = destination;
		}
	}, [isLoading, isAuthenticated, scopes, returnTo]);

	// -----------------------------------------------------------------------
	// Handlers — OTP login
	// -----------------------------------------------------------------------

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) {
		e.preventDefault();
		e.stopPropagation();
		setLoading(true);

		try {
			await api.auth.otp.send({ email });

			// Seed the countdown so the verify page's "Send Again" button
			// starts disabled — the OTP was just sent.
			sessionStorage.setItem('otp-timer', '60');

			const params = new URLSearchParams({ email });
			if (returnTo) params.set('returnTo', returnTo);

			navigate(`/verify?${params.toString()}`);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to send verification code. Please try again.');
			setLoading(false);
		}
	}

	function handleGoogleLogin() {
		window.location.href = `${window.location.origin}/api/auth/google`;
	}

	async function handlePasskeyLogin() {
		if (!window.PublicKeyCredential) {
			toast.error('Passkeys are not supported in this browser.');
			return;
		}
		setPasskeyLoading(true);
		const { data: optionsData, error: optionsErr } = await tryCatch(api.auth.passkeyOptions());
		if (optionsErr || !optionsData?.options) {
			toast.error(optionsErr instanceof Error ? optionsErr.message : 'Failed to get passkey options.');
			setPasskeyLoading(false);
			return;
		}
		const optionsJson = optionsData.options as {
			challenge: string;
			allowCredentials?: { id: string; transports?: string[] }[];
			[key: string]: unknown;
		};
		const options: PublicKeyCredentialRequestOptions = {
			...optionsJson,
			challenge: base64urlToArrayBuffer(optionsJson.challenge),
			allowCredentials: optionsJson.allowCredentials?.map((c) => ({
				type: 'public-key' as const,
				id: base64urlToArrayBuffer(c.id),
				transports: c.transports as AuthenticatorTransport[],
			})),
		};
		let credential: PublicKeyCredential;
		try {
			credential = (await navigator.credentials.get({ publicKey: options })) as PublicKeyCredential;
		} catch (err) {
			if (err instanceof Error && err.name === 'NotAllowedError') {
				toast.info('Sign-in cancelled.');
			} else {
				toast.error(err instanceof Error ? err.message : 'Failed to sign in with passkey.');
			}
			setPasskeyLoading(false);
			return;
		}
		if (!credential) {
			setPasskeyLoading(false);
			return;
		}
		const assertionResponse = credential.response as AuthenticatorAssertionResponse;
		const credentialPayload = {
			id: credential.id,
			rawId: arrayBufferToBase64url(credential.rawId),
			type: 'public-key' as const,
			response: {
				clientDataJSON: arrayBufferToBase64url(assertionResponse.clientDataJSON),
				authenticatorData: arrayBufferToBase64url(assertionResponse.authenticatorData),
				signature: arrayBufferToBase64url(assertionResponse.signature),
				...(assertionResponse.userHandle && { userHandle: arrayBufferToBase64url(assertionResponse.userHandle) }),
			},
		};
		try {
			const result = await api.auth.passkeyVerify({ credential: credentialPayload });
			await storage.local.set('accessToken', result.tokens.accessToken);
			await storage.local.set('refreshToken', result.tokens.refreshToken);
			if (result.tokens.sessionId) {
				await storage.local.set('currentSessionId', result.tokens.sessionId);
			}
			await saveAccount({
				id: result.user.id,
				email: result.user.email,
				name: result.user.name,
				avatarUrl: result.user.avatarUrl,
				refreshToken: result.tokens.refreshToken,
			});
			if (returnTo) {
				window.location.href = returnTo;
			} else {
				const destination = await resolvePostLoginScopes(result.tokens.accessToken);
				window.location.href = destination;
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Passkey sign-in failed.');
		}
		setPasskeyLoading(false);
	}

	// -----------------------------------------------------------------------
	// Handlers — saved account resume
	// -----------------------------------------------------------------------

	async function handleResume(account: SavedAccount) {
		setResumingId(account.id);

		// Place the saved refresh token into the active slot and attempt
		// a token rotation. The server issues a fresh token pair if the
		// stored refresh token is still valid.
		await storage.local.set('refreshToken', account.refreshToken);

		const { data: response, error: fetchErr } = await tryCatch(
			fetch('/api/auth/refresh', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ refreshToken: account.refreshToken }),
			}),
		);

		if (fetchErr || !response || !response.ok) {
			// Refresh token expired or revoked — remove the stale entry
			// and automatically start the OTP login flow for this email.
			await removeAccount(account.id);
			await refreshSavedAccounts();
			await storage.local.delete('refreshToken');

			const { error: otpErr } = await tryCatch(api.auth.otp.send({ email: account.email }));
			if (otpErr) {
				// OTP send failed — fall back to prefilling the email field.
				setEmail(account.email);
				setResumingId(null);
				toast.error('Could not start login automatically. Please try again.');
				return;
			}

			sessionStorage.setItem('otp-timer', '60');

			const params = new URLSearchParams({ email: account.email });
			if (returnTo) params.set('returnTo', returnTo);
			navigate(`/verify?${params.toString()}`);
			return;
		}

		const { data: body, error: jsonErr } = await tryCatch(
			response.json() as Promise<{
				tokens: {
					accessToken: string;
					refreshToken: string;
					sessionId?: string;
				};
			}>,
		);

		if (jsonErr || !body?.tokens?.accessToken) {
			await removeAccount(account.id);
			await refreshSavedAccounts();
			await storage.local.delete('refreshToken');

			const { error: otpErr } = await tryCatch(api.auth.otp.send({ email: account.email }));
			if (otpErr) {
				setEmail(account.email);
				setResumingId(null);
				toast.error('Could not start login automatically. Please try again.');
				return;
			}

			sessionStorage.setItem('otp-timer', '60');

			const params = new URLSearchParams({ email: account.email });
			if (returnTo) params.set('returnTo', returnTo);
			navigate(`/verify?${params.toString()}`);
			return;
		}

		// Persist the fresh token pair and current session id.
		await storage.local.set('accessToken', body.tokens.accessToken);
		await storage.local.set('refreshToken', body.tokens.refreshToken);
		if (body.tokens.sessionId) {
			await storage.local.set('currentSessionId', body.tokens.sessionId);
		}

		// Update the registry with the rotated refresh token so it stays
		// in sync even if the redirect is slow.
		await saveAccount({
			id: account.id,
			email: account.email,
			name: account.name,
			avatarUrl: account.avatarUrl,
			refreshToken: body.tokens.refreshToken,
		});

		// Resolve where to send the user based on their scopes.
		if (returnTo) {
			window.location.href = returnTo;
		} else {
			const destination = await resolvePostLoginScopes(body.tokens.accessToken);
			window.location.href = destination;
		}
	}

	async function handleRemoveAccount(accountId: string) {
		await removeAccount(accountId);
		await refreshSavedAccounts();
	}

	// -----------------------------------------------------------------------
	// Guard — show spinner while checking existing auth
	// -----------------------------------------------------------------------

	if (isLoading || isAuthenticated) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		);
	}

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="flex h-screen w-full items-center justify-center px-4">
			<Card className="w-full max-w-md overflow-hidden">
				<CardContent className="grid p-0 md:grid-cols-1">
					<div className="p-6 md:p-8">
						<div className="flex flex-col gap-6">
							<div className="flex flex-col items-center text-center">
								<BrandLogo className="mb-6" />
								<p className="text-balance text-muted-foreground">
									{hasSavedAccounts ? 'Choose an account to continue' : 'Login to your LearnAI account'}
								</p>
							</div>

							{/* ── Saved accounts quick-login ──────────────── */}
							{hasSavedAccounts && (
								<>
									<div className="flex flex-col gap-2">
										{savedAccounts.map((account) => {
											const displayName = account.name ?? account.email;
											const isResuming = resumingId === account.id;

											return (
												<ButtonGroup key={account.id} className="w-full">
													<Button
														variant="outline"
														type="button"
														onClick={() => handleResume(account)}
														disabled={isBusy}
														className="h-auto flex-1 justify-start gap-3 px-3 py-2.5"
													>
														<Avatar size="sm">
															{account.avatarUrl && <AvatarImage src={account.avatarUrl} alt={displayName} />}
															<AvatarFallback>{getUserInitials(account.name, account.email)}</AvatarFallback>
														</Avatar>
														<div className="min-w-0 flex-1 text-left">
															<p className="truncate text-sm font-medium">{displayName}</p>
															{account.name && (
																<p className="truncate text-xs font-normal text-muted-foreground">{account.email}</p>
															)}
														</div>
														{isResuming && <Spinner className="size-4 shrink-0" />}
													</Button>
													<Button
														variant="outline"
														size="icon"
														type="button"
														onClick={() => handleRemoveAccount(account.id)}
														disabled={isBusy}
														className="h-auto text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
														aria-label={`Remove ${displayName}`}
													>
														<XIcon className="size-3.5" />
													</Button>
												</ButtonGroup>
											);
										})}
									</div>

									<div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
										<span className="relative z-10 bg-card px-2 text-muted-foreground">Or use a different email</span>
									</div>
								</>
							)}

							{/* ── Email / OTP form ───────────────────────── */}
							<form onSubmit={handleSubmit}>
								<div className="flex flex-col gap-6">
									<div className="grid gap-2">
										<Label htmlFor="email">Email</Label>
										<Input
											id="email"
											name="email"
											type="email"
											placeholder="m@example.com"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											required
											autoFocus={!hasSavedAccounts}
											autoComplete="email"
										/>
									</div>

									<Button type="submit" className="w-full" disabled={isBusy || !email}>
										{loading ? <Spinner className="size-3" /> : 'Login'}
									</Button>
								</div>
							</form>

							{/* ── OAuth providers ────────────────────────── */}
							<div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
								<span className="relative z-10 bg-card px-2 text-muted-foreground">Or continue with</span>
							</div>

							<div className="grid grid-cols-1 gap-4">
								<Button
									variant="outline"
									type="button"
									className="w-full"
									onClick={handlePasskeyLogin}
									disabled={isBusy}
								>
									{passkeyLoading ? <Spinner className="size-4" /> : <KeyIcon className="size-4" />}
									<span>Sign in with passkey</span>
								</Button>
								<Button variant="outline" type="button" className="w-full" onClick={handleGoogleLogin}>
									<GoogleIcon />
									<span>Google</span>
								</Button>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
